import Bowser from 'bowser'

import { singletonRouter } from '@hozana/router'
import { mobileDetect } from '@hozana/screen/functions/mobileDetect'
import { LOCAL_STORAGE } from '@hozana/storage/browserStorages'
import { cookie } from '@hozana/storage/cookies'
import { GTM, type TGTMEventObjet } from '@hozana/tracking/gtm'

import { COOKIES, COOKIES_CONFIG } from 'general/managers/cookies/constants'
import type { TLocale } from 'i18n/constants'
import { newRoutes } from 'routes/constants'

export function getDeviceInfo(props?: { userAgent?: string; getObject: true }): Bowser.Parser.ParsedResult
export function getDeviceInfo(props?: { userAgent?: string; getObject?: false }): string
export function getDeviceInfo({
  userAgent = __CLIENT__ ? window.navigator.userAgent : '',
  getObject,
}: { userAgent?: string; getObject?: boolean } = {}) {
  const data = Bowser.parse(userAgent)

  if (getObject) {
    return data
  }

  return [
    data.platform.type && data.platform.type.replace(/^./, (match) => match.toUpperCase()),
    data.platform.vendor,
    data.platform.model,
    data.os.name,
    data.os.version,
    data.browser.name && `- ${data.browser.name}`,
  ]
    .filter(Boolean)
    .join(' ')
}

export const getStoredLang = () =>
  typeof window !== 'undefined' ? (window.localStorage.getItem(LOCAL_STORAGE.LANG) as TLocale | undefined) : undefined

export const switchLang = (locale: TLocale) => {
  window.localStorage.setItem(LOCAL_STORAGE.LANG, locale)
  cookie.save(COOKIES.hideBannerLang, true, COOKIES_CONFIG.ONE_YEAR)
}

/**************************************************/
/*** Connect details and redirection on connect ***/
/**************************************************/

export type TConnectDetails = {
  displayWizardOnSignup?: boolean
  redirectUrl?: string
  /** No redirection if set to true */
  skipRedirect?: boolean
  socialName?: string
  locale?: TLocale
  newUser?: boolean
  email?: string
  reload?: boolean
  /**
   * Used instead of redirectUrl:
   * - when the app just reloaded (on social redirection on mobile or on reset password)
   * - or when the app will reload (if reload is set to true)
   */
  reloadUrl?: string
  /** GTM data to be sent before redirecting */
  gtmData?: TGTMEventObjet[]
}

/**
 * Saves connect details as a cookie to handle redirection on connect.
 */
export const saveConnectDetails = (connectDetails: TConnectDetails): void =>
  cookie.save(COOKIES.connectDetails, JSON.stringify(connectDetails), COOKIES_CONFIG.THIRTY_MINUTES_SECURE)

/**
 * Get connect details previously stored in cookies.
 */
export const getConnectDetails = () => cookie.load<TConnectDetails>(COOKIES.connectDetails) || {}

/**
 * Remove connect details cookie.
 */
export const removeConnectDetails = (): void => cookie.remove(COOKIES.connectDetails, COOKIES_CONFIG.SESSION)

/**
 * Update a value stored in the connect details cookie
 *
 * ⚠ `updateConnectDetails({ redirectUrl: undefined })` will indeed set redirectUrl to undefined
 */
export const updateConnectDetails = (connectDetails: TConnectDetails): void => {
  const currentConnectDetails = getConnectDetails()
  saveConnectDetails({
    ...currentConnectDetails,
    ...connectDetails,
    ...(connectDetails.gtmData || currentConnectDetails.gtmData
      ? { gtmData: [...(currentConnectDetails.gtmData || []), ...(connectDetails.gtmData || [])] }
      : {}),
  })
}

/**
 * Redirect after connection using the connectDetails cookie.
 */
export const redirect = async (connectDetails: TConnectDetails = {}): Promise<boolean> => {
  const {
    redirectUrl,
    reload,
    reloadUrl,
    socialName,
    locale = singletonRouter.locale,
    gtmData,
  } = { ...getConnectDetails(), ...connectDetails }

  // Remove connect details cookie to avoid interferences later.
  removeConnectDetails()

  /** Has already reloaded (connection on mobile device) */
  const hasReloaded = socialName && mobileDetect(window.navigator.userAgent)
  /** Should reload now */
  const shouldReload = reload || !redirectUrl
  /** Has already reloaded or will reload now */
  const isReload = shouldReload || hasReloaded

  const url = (isReload && reloadUrl) || redirectUrl || newRoutes.prayerSpace()

  if (gtmData) {
    GTM.push(...gtmData)
  }

  if (shouldReload) {
    // Add a delay before redirecting to ensure request to GTM is not cancelled
    await new Promise((resolve) => setTimeout(resolve, 300))
    window.location.replace(url)
    return true
  }

  if (!redirectUrl) {
    return true
  }

  return await singletonRouter.replace(url, undefined, { shallow: true, locale })
}
